import axios from 'axios';
import base64 from 'base-64';

const api = axios.create({
  baseURL:
    process.env.NODE_ENV === 'development' ? `${process.env.API_URL}/public` : process.env.API_URL,
  json: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export const verifyToken = (accessToken, status) =>
  api.post('/verify-token', { token: accessToken, status });

export const refreshToken = (accessToken) => api.post('/refresh-token', { token: accessToken });

export const getCurrentTeam = (jwt) =>
  api.get('/v2/teams/current', {
    headers: { Authorization: jwt },
  });

export const getProfile = (jwt, internalId, locale, adminRequestedStreetlibInternalId) =>
  api.get(
    `/users/${base64.encode(internalId)}?locale=${locale}${adminRequestedStreetlibInternalId
      ? `&adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    {
      headers: { Authorization: jwt },
    },
  );

export const getProfileOnboard = (jwt, internalId, locale, adminRequestedStreetlibInternalId) =>
  api.get(
    `/users/${base64.encode(internalId)}/onboard?locale=${locale}${adminRequestedStreetlibInternalId
      ? `&adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    {
      headers: { Authorization: jwt },
    },
  );

export const postAcceptContract = async (jwt, internalId, body) => {
  const encodedId = base64.encode(internalId);
  console.log('body', body);

  if (body.service === 'DASH') {
    // Effettua la richiesta PATCH per DASH
    const url = `/users/${encodedId}/register`;

    const payload = {
      user: {
        platform: 'DASH',
        user_guid: body.guid || '', // Assicuriamoci che user_guid sia presente
      },
    };

    return api.patch(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: jwt, // Se il JWT è necessario
      },
    });
  }

  // Default: POST per SHUFFLE e SHUFFLE_WRITE
  let action = 'shuffle';
  let payload = body;

  if (body.service === 'SHUFFLE_WRITE') {
    action = 'multi_platform_shuffle';
    payload = { platform: 'SHUFFLE_WRITE' };
  }

  return api.post(`/users/${encodedId}/${action}`, payload, {
    headers: { Authorization: jwt },
  });
};

export const getBillingDocuments = (jwt, billingId, adminRequestedStreetlibInternalId, params) =>
  api.get(`/bill/v4/customers/${billingId}/invoiceDocuments`, {
    headers: { Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
      ...params,
    },
  });

export const getBillingProfiles = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
) =>
  api.get(`/bill/v4/customers/${billingId}/billingProfiles`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getBillingProfile = (
  languageCode,
  jwt,
  billingId,
  pid,
  adminRequestedStreetlibInternalId,
) =>
  api.get(`/bill/v4/customers/${billingId}/billingProfiles/${pid}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getBillingSummary = (jwt, billingId, adminRequestedStreetlibInternalId) =>
  api.get(`/bill/v4/customers/${billingId}/invoiceDocuments/summary`, {
    headers: { Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getCount = (jwt, params) =>
  api.get(`/analytics/count?${params}`, {
    headers: { Authorization: jwt },
  });

export const getHistogram = (jwt, params) =>
  api.get(`/analytics/histogram?${params}`, {
    headers: { Authorization: jwt },
  });

export const getAggr = (jwt, params) =>
  api.get(`/analytics/aggs?${params}`, {
    headers: { Authorization: jwt },
  });

export const getUnits = (jwt, params) =>
  api.get(`/analytics/search?${params}`, {
    headers: { Authorization: jwt },
  });

export const getHistory = (jwt, internalId, adminRequestedStreetlibInternalId) =>
  api.get(
    `/users/${base64.encode(internalId)}/history${adminRequestedStreetlibInternalId
      ? `?adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    {
      headers: { Authorization: jwt },
    },
  );

// Analytics downloads (CSV/XLSX built in the background) go through publish-api;
// they replaced streetlib-queues in October 2026. Two doors, decided by the address
// and never by a body field: the caller's own data, or every account (admin with
// staff:bi:read). An admin acting as a user (adminRequestedStreetlibInternalId)
// exports that user's data through the own-data door. The sagas keep queues' shape:
// the create call answers `params.key`, and the status call throws (status 425,
// "not yet") until the file is ready, which is what their retry loop waits on.
const OWN_EXPORTS_PATH = '/publish/v4/analytics/download-exports';
const ALL_ACCOUNTS_EXPORTS_PATH = '/publish/v4/admin/analytics/download-exports';

const createExport = (kind) => (jwt, params, payload = {}) => {
  const { isAdmin, format, ...query } = payload || {};
  const actingAsUser = params.includes('adminRequestedStreetlibInternalId=');
  const path = isAdmin && !actingAsUser ? ALL_ACCOUNTS_EXPORTS_PATH : OWN_EXPORTS_PATH;
  return api
    .post(`${path}?${params}`, { ...query, kind, file_format: format || 'csv' }, {
      headers: { Authorization: jwt },
    })
    .then((res) => ({ ...res, data: { params: { key: res.data && res.data.data && res.data.data.id } } }));
};

const getExport = (jwt, key) =>
  api
    .get(`${OWN_EXPORTS_PATH}/${key}`, {
      headers: { Authorization: jwt },
    })
    .then((res) => {
      const status = (res.data && res.data.data) || {};
      if (status.state === 'done' && status.url) return { ...res, data: { url: status.url } };
      const error = new Error(`analytics export ${status.state || 'unknown'}`);
      // A failed export never turns done: no response, so the saga reports it at once
      // instead of retrying until its timeout.
      if (status.state !== 'failed') error.response = { status: 425 };
      throw error;
    });

export const postDownloadJob = createExport('transactions');

export const getDownloadJob = getExport;

export const postUnitsExportJob = createExport('units');

export const getUnitsExportJob = getExport;

export const getWildcard = (
  jwt,
  {
    adminRequestedStreetlibInternalId,
    dateFrom,
    dateTo,
    currency,
    offset,
    term,
    aggrTerm,
    wildcard,
  },
) =>
  api.get(
    `/analytics/aggs?currency=${currency}&dateFrom=${dateFrom}&dateTo=${dateTo}&size=100&offset=${offset}&term=${aggrTerm}&filters=${term}=${wildcard}${adminRequestedStreetlibInternalId
      ? `&adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    {
      headers: { Authorization: jwt },
    },
  );

export const getWarning = () => api.get('/notifications?enabled=true');

export const getNews = (languageCode) =>
  api.get(
    `https://streetlib.ghost.io/ghost/api/v3/content/posts/?limit=3&filter=tag:hash-${languageCode}&include=tags&key=${process.env.GHOST_ID}`,
  );

export const getSettings = (jwt, internalId, adminRequestedStreetlibInternalId) =>
  api.get(
    `/users/${base64.encode(internalId)}/settings${adminRequestedStreetlibInternalId
      ? `?adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    {
      headers: { Authorization: jwt },
    },
  );

export const putPreset = (jwt, internalId, adminRequestedStreetlibInternalId, body) =>
  api.patch(
    `/users/${base64.encode(internalId)}${adminRequestedStreetlibInternalId
      ? `?adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    body,
    {
      headers: { Authorization: jwt },
    },
  );

export const getComparedTo = (jwt, dateFrom, dateTo, compare, dateRange) =>
  api.get(
    `/analytics/aggs?compare=${compare}&size=1&dateTo=${dateTo}&dateFrom=${dateFrom}&dateRange=${dateRange}&version=7`,
    {
      headers: { Authorization: jwt },
    },
  );

export const getCountries = (locale) => {
  const newLocale = locale === 'pt' || locale === 'de' ? 'en' : locale;
  return api.get(`/countries?locale=${newLocale}`);
};

export const getTimezones = (jwt) =>
  api.get('/timezones', {
    headers: { Authorization: jwt },
  });

export const getTokenActivation = (jwt, token) =>
  api.get(`/tokens/${token}/activate`, {
    headers: { Authorization: jwt },
  });

export const patchProfile = (jwt, internalId, adminRequestedStreetlibInternalId, body) =>
  api.patch(
    `/users/${base64.encode(internalId)}${adminRequestedStreetlibInternalId
      ? `?adminRequestedStreetlibInternalId=${adminRequestedStreetlibInternalId}`
      : ''
    }`,
    body,
    {
      headers: { Authorization: jwt },
    },
  );

// A person's own password, through dash-api: Cognito's ChangePassword with the
// caller's own token and their current password.
//
// It replaced userbridge's /passwords/change (8 October 2026), which set a password
// from the account id alone — no current password, and no check at all for anybody
// calling it from inside the VPC. There is deliberately no impersonation parameter:
// the token is always the signed-in person's, so under impersonation this would
// change the operator's own password. Operators reset a customer's password in OS.
export const putPassword = (jwt, body) =>
  api.put('/v2/user/password', body, {
    headers: { Authorization: jwt },
  });

export const patchAvatar = (jwt, internalId, body) =>
  api.patch(`/users/${base64.encode(internalId)}/avatar`, body, {
    headers: { Authorization: jwt },
  });

export const deleteAvatar = (jwt, internalId) =>
  api.delete(`/users/${base64.encode(internalId)}/avatar`, {
    headers: { Authorization: jwt },
  });

export const postNewsletter = (jwt, internalId, body) =>
  api.post(`/users/${base64.encode(internalId)}/newsletter`, body, {
    headers: { Authorization: jwt },
  });

export const deleteNewsletter = (jwt, internalId, body) =>
  api.delete(`/users/${base64.encode(internalId)}/newsletter`, {
    headers: { Authorization: jwt },
    data: body,
  });

export const getStores = () => api.get('/analytics/stores');

export const getReportStatuses = (languageCode, jwt, adminRequestedStreetlibInternalId) =>
  api.get(`/publish/analytics/reports/sales/statuses`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getInvoice = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  pid,
  billing_profile_pid,
) =>
  api.get(`/bill/v4/customers/${billingId}/invoiceDocuments/${pid}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
      billing_profile_pid,
    },
  });

export const patchBillingProfile = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  pid,
  body,
) =>
  api.patch(`/bill/v4/customers/${billingId}/billingProfiles/${pid}`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const postBillingProfile = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  body,
) =>
  api.post(`/bill/v4/customers/${billingId}/billingProfiles`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const deleteBillingProfile = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  profileId,
) =>
  api.delete(`/bill/v4/customers/${billingId}/billingProfiles/${profileId}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getPaymentMethods = (jwt) =>
  api.get('/bill/v4/paymentMethods', {
    headers: { Authorization: jwt },
  });

export const getPaymentAccounts = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
) =>
  api.get(`/bill/v4/customers/${billingId}/paymentAccounts`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getPaymentAccount = (
  languageCode,
  jwt,
  billingId,
  pid,
  adminRequestedStreetlibInternalId,
) =>
  api.get(`/bill/v4/customers/${billingId}/paymentAccounts/${pid}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const postPaymentAccount = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  body,
) =>
  api.post(`/bill/v4/customers/${billingId}/paymentAccounts`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const patchPaymentAccount = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  paymentAccountId,
  body,
) =>
  api.patch(`/bill/v4/customers/${billingId}/paymentAccounts/${paymentAccountId}`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const deletePaymentAccount = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  paymentAccountId,
) =>
  api.delete(`/bill/v4/customers/${billingId}/paymentAccounts/${paymentAccountId}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      adminRequestedStreetlibInternalId,
    },
  });

export const getToken = (languageCode, jwt, userPid, tokenPid) =>
  api.get(`/bill/v4/users/${userPid}/tokens/${tokenPid}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
  });

export const patchTokenActivate = (languageCode, jwt, userPid, tokenPid, body) =>
  api.patch(`/bill/v4/users/${userPid}/tokens/${tokenPid}/activate`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
  });

export const patchTokenReset = (languageCode, jwt, userPid, tokenPid) =>
  api.patch(
    `/bill/v4/users/${userPid}/tokens/${tokenPid}/reset`,
    undefined,
    {
      headers: { 'Accept-Language': languageCode, Authorization: jwt },
    },
  );

// `date` e' opzionale ma va passata per le province di NASCITA: il backend
// ricava l'elenco unendo i comuni validi a quella data, e senza parametro usa
// oggi. Le province italiane sono cambiate nel tempo (soppressioni, nuove
// istituzioni, rinomine), quindi l'elenco odierno non copre chi e' nato prima.
export const getSubdivisions = (languageCode, jwt, countryCode, date) =>
  api.get(`/bill/v4/countries/${countryCode}/subdivisions`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    ...(date ? { params: { date } } : {}),
  });

// Comuni di una provincia, validi a una certa data.
// Serve a popolare il comune di nascita: il backend risolve il codice
// catastale confrontando la denominazione ESATTA (ILIKE, senza jolly) e non
// guarda mai la denominazione alternativa, quindi il valore inviato deve
// arrivare da questo elenco e non essere digitato a mano.
export const getMunicipalities = (languageCode, jwt, countryCode, subdivisionCode, date) =>
  api.get(`/bill/v4/countries/${countryCode}/subdivisions/${subdivisionCode}/municipalities`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    ...(date ? { params: { date } } : {}),
  });

export const getTaxDocuments = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  page,
  per_page,
) =>
  api.get(`/bill/v4/customers/${billingId}/taxDocuments`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: {
      page,
      per_page,
      adminRequestedStreetlibInternalId,
    },
  });

export const downloadTaxDocument = (languageCode, jwt, adminRequestedStreetlibInternalId, path) =>
  axios.get(path, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const approveInvoiceDocument = (
  languageCode,
  jwt,
  billingId,
  adminRequestedStreetlibInternalId,
  pid,
  body,
) =>
  api.post(`/bill/v4/customers/${billingId}/invoiceDocuments/${pid}/approve`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const downloadInvoice = (languageCode, jwt, adminRequestedStreetlibInternalId, path) =>
  axios.get(path, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const downloadReportInvoice = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  pid,
  format,
  additionalParams = {},
) =>
  api.get(`/bill/v4/customers/${billingId}/invoiceDocuments/${pid}/analyticsReport/${format}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId, ...additionalParams },
  });

export const getTaxInterviews = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  page,
  per_page,
) =>
  api.get(`/bill/v4/customers/${billingId}/taxDocuments`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { page, per_page, adminRequestedStreetlibInternalId },
  });

export const postTaxInterview = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  body,
) =>
  api.post(`/bill/v4/customers/${billingId}/taxDocuments`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const patchTaxInterview = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  pid,
  body,
) =>
  api.patch(`/bill/v4/customers/${billingId}/taxDocuments/${pid}`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const downloadTaxInterview = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  pid,
) =>
  api.get(`/bill/v4/customers/${billingId}/taxDocuments/${pid}/downloadLink`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const getCountryWithheldDetails = (languageCode, jwt, countryCode) =>
  api.get(`/bill/v4/countryWithhelds/${countryCode}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
  });

export const isWithrawAvailable = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
) =>
  api.get(`/bill/v4/customers/${billingId}/isWithrawAvailable`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const getWithdrawals = (languageCode, jwt, adminRequestedStreetlibInternalId, billingId) =>
  api.get(`/bill/v4/customers/${billingId}/withdrawals`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const getLastWithdrawal = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
) =>
  api.get(`/bill/v4/customers/${billingId}/withdrawals/last`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const postWithdrawal = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  billingId,
  body,
) =>
  api.post(`/bill/v4/customers/${billingId}/withdrawals`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const checkPayeeId = (languageCode, jwt, payeeId) =>
  api.get(`/bill/v4/misc/payoneer/checkPayee?payeeIdentifier=${payeeId}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
  });

export const postPayoneerRegistration = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  body,
) =>
  api.post(`/bill/v4/misc/payoneer/registrationLink`, body, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { adminRequestedStreetlibInternalId },
  });

export const checkTin = (languageCode, jwt, countryCode, tin, type = null) =>
  api.get(`/bill/v4/misc/tinCheck`, {
    params: { country_code: countryCode, tin, typology: type },
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
  });

export const getAnalyticsFilters = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  type,
  params,
) =>
  api.get(`/publish/analytics/publish/${type}`, {
    headers: { 'Accept-Language': languageCode, Authorization: jwt },
    params: { ...params, adminRequestedStreetlibInternalId },
  });

export const generateInvoicingReport = (
  languageCode,
  jwt,
  adminRequestedStreetlibInternalId,
  startDate,
  endDate,
) =>
  api.post(
    '/publish/invoicing/reports/invoicing_details',
    {
      start_date: startDate,
      end_date: endDate,
    },
    {
      headers: { 'Accept-Language': languageCode, Authorization: jwt },
      params: {
        adminRequestedStreetlibInternalId,
      },
    },
  );

// StreetLib One, through dash-api's own till: authenticated, and the plan comes from
// our catalogue rather than from a product code the caller chooses.
//
// Which of the two tills to use is NOT decided here and not by a build variable: the
// profile carries `offer`, dash-api answers it from one date, and this file only obeys.
export const createOneCheckoutSession = (jwt, body) =>
  api.post('/v2/user/checkout_sessions', body, {
    headers: { Authorization: jwt },
  });

export const createCheckoutSession = (jwt, body) => {
  // Checkout endpoint uses base URL without /public (stripe is not under /public)
  const baseURL = (process.env.API_URL || '').replace(/\/public\/?$/, '');
  const checkoutApi = axios.create({
    baseURL,
    json: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: jwt,
    },
  });

  return checkoutApi.post('/stripe/checkout', body);
};
